import {
  resolveFormOptionValues,
  resolveQuestionIdentity,
  resolveQuestionRef,
} from './create_sections_question_helpers.js';

function ensureQuestionOptionInputs(form) {
  const has_option_inputs = Array.isArray(form.option_inputs) && form.option_inputs.length;
  if (!has_option_inputs) {
    form.option_inputs = ['', ''];
  }
}

function normalizeDependantQuestionState(form, requires_options) {
  if (!requires_options) {
    form.dependant_questions = [];
    return;
  }

  if (!Array.isArray(form.dependant_questions)) {
    form.dependant_questions = [];
  }
}

export function applyQuestionTypeState(ctx, form, selected_type) {
  const requires_options = ctx.question_type_requires_options(selected_type?.question_type);
  form.show_option_inputs = requires_options;
  form.multi_select_options = requires_options
    ? resolveFormOptionValues(form.option_inputs)
    : [];
  if (requires_options) {
    ensureQuestionOptionInputs(form);
  }
  normalizeDependantQuestionState(form, requires_options);
}

function resolveEditQuestionOptionValues(question = {}) {
  const option_values = [
    question?.options,
    question?.multi_select_options,
    question?.multi_select_choices,
    question?.choices,
    question?.question_options,
    question?.option_values,
    question?.question_choices,
    [],
  ].find((value) => value != null);
  return option_values;
}

function resolveNormalizedQuestionTypeId(question_data, normalized_question) {
  const question_type_id = question_data?.question_type?.id
    ?? question_data?.question_type_id
    ?? normalized_question.question_type_id;
  return question_type_id;
}

function resolveNormalizedMandatoryState(question_data, normalized_question) {
  const is_mandatory = typeof question_data?.is_mandatory === 'boolean'
    ? question_data.is_mandatory
    : normalized_question.is_mandatory;
  return is_mandatory;
}

function resolveNormalizedDependantQuestions(question_data, normalized_question) {
  const dependant_questions = Array.isArray(question_data?.dependant_questions)
    ? question_data.dependant_questions
    : normalized_question.dependant_questions;
  return dependant_questions;
}

function resolveQuestionTextValue(question_data, normalized_question) {
  return question_data.question_text ?? normalized_question.question_text;
}

function resolveQuestionDescriptionValue(question_data, normalized_question) {
  return (
    question_data.question_description ??
    normalized_question.question_description
  );
}

function resolveQuestionNumberValue(question_data, normalized_question) {
  return question_data?.question_number ?? normalized_question.question_number ?? '';
}

function buildNormalizedEditQuestionFields(question_data, normalized_question) {
  const normalized_edit_question_fields = {
    question_text: resolveQuestionTextValue(question_data, normalized_question),
    question_description: resolveQuestionDescriptionValue(
      question_data,
      normalized_question
    ),
    question_type_id: resolveNormalizedQuestionTypeId(
      question_data,
      normalized_question
    ),
    is_mandatory: resolveNormalizedMandatoryState(
      question_data,
      normalized_question
    ),
    question_number: resolveQuestionNumberValue(
      question_data,
      normalized_question
    ),
    dependant_questions: resolveNormalizedDependantQuestions(
      question_data,
      normalized_question
    ),
  };
  return normalized_edit_question_fields;
}

function buildNormalizedEditQuestionBase(ctx, normalized_question, question_id) {
  const normalized_edit_question_base = {
    ...ctx.current_edit_question,
    ...normalized_question,
    question_id,
  };
  return normalized_edit_question_base;
}

export function buildNormalizedEditQuestionPayload(
  ctx,
  question_data,
  normalized_question,
  question_id
) {
  const normalized_edit_question_payload = {
    ...buildNormalizedEditQuestionBase(ctx, normalized_question, question_id),
    ...buildNormalizedEditQuestionFields(question_data, normalized_question),
  };
  return normalized_edit_question_payload;
}

function buildQuestionOptionState(question, option_inputs) {
  const multi_select_options = Array.isArray(question?.multi_select_options)
    ? question.multi_select_options
    : option_inputs.filter(Boolean);
  const dependant_questions = Array.isArray(question?.dependant_questions)
    ? question.dependant_questions
    : Array.isArray(question?.dependent_questions)
      ? question.dependent_questions
      : [];
  const question_option_state = {
    multi_select_options,
    dependant_questions,
  };
  return question_option_state;
}

function buildQuestionIdentityMetadata(index, question_id) {
  const question_identity_metadata = {
    id: question_id ?? `${Date.now()}-${index}`,
    question_id,
  };
  return question_identity_metadata;
}

function resolveQuestionText(question) {
  return (
    question?.question_text ??
    question?.question ??
    question?.text ??
    question?.name ??
    ''
  );
}

function resolveQuestionDescription(question) {
  return question?.question_description ?? question?.description ?? '';
}

function resolveMandatoryValue(question) {
  return typeof question?.is_mandatory === 'boolean'
    ? question.is_mandatory
    : true;
}

function buildQuestionIdentityQuestionContent(question, question_type_id) {
  const question_identity_question_content = {
    question_text: resolveQuestionText(question),
    question_description: resolveQuestionDescription(question),
    question_type_id,
  };
  return question_identity_question_content;
}

function buildQuestionIdentityFlags(question) {
  const question_identity_flags = {
    is_mandatory: resolveMandatoryValue(question),
    is_unique: Boolean(question?.is_unique),
    question_number: question?.question_number ?? '',
  };
  return question_identity_flags;
}

export function buildQuestionIdentityContent(question, question_type_id) {
  const question_identity_content = {
    ...buildQuestionIdentityQuestionContent(question, question_type_id),
    ...buildQuestionIdentityFlags(question),
  };
  return question_identity_content;
}

function buildQuestionIdentityState(question, index, question_id, question_type_id) {
  const question_identity_state = {
    ...buildQuestionIdentityMetadata(index, question_id),
    ...buildQuestionIdentityContent(question, question_type_id),
  };
  return question_identity_state;
}

export function buildOptionInputsFromValues(ctx, values = []) {
  const options = Array.isArray(values)
    ? values.map((option) => ctx.extract_choice_label(option)).filter(Boolean)
    : [];
  return options.length >= 2 ? options : ['', ''];
}

export function extractQuestionOptionValues(question = {}) {
  const option_values = resolveEditQuestionOptionValues(question);
  return option_values;
}

export function buildEditQuestionForm(question = {}, index = 0, ctx) {
  const question_id = resolveQuestionIdentity(question);
  const question_type_id = question?.question_type_id ?? question?.question_type?.id ?? question?.type ?? '';
  const option_inputs = buildOptionInputsFromValues(ctx, extractQuestionOptionValues(question));
  const show_option_inputs = ctx.question_type_requires_options(ctx.get_selected_question_raw_type(question_type_id));
  const question_identity_state = buildQuestionIdentityState(question, index, question_id, question_type_id);
  const question_option_state = buildQuestionOptionState(question, option_inputs);
  const edit_question_form = {
    ...question_identity_state,
    show_option_inputs,
    option_inputs,
    ...question_option_state,
  };
  return edit_question_form;
}

export function resolveEditQuestionModalState(ctx, section_ref, question_ref) {
  const section = section_ref && typeof section_ref === 'object' ? section_ref : ctx.sections[Number(section_ref)];
  const question = resolveQuestionRef(section, question_ref);
  const question_id = resolveQuestionIdentity(question);
  const modal_state = {
    section,
    question,
    question_id,
  };
  return modal_state;
}
